import { useCallback, useEffect, useRef, useState } from 'react';
import adminSalesEventService from '../../services/adminSalesEventService';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { WHATSAPP_TEMPLATE_STATUS } from '../../utils/whatsapp';

/**
 * Loads one thing once `enabled` is true and exposes {data, loading, error, reload}. A failed load
 * keeps its message (and never a toast: the step shows it next to a retry button).
 */
const useLookup = (fetcher, enabled = true) => {
  const [state, setState] = useState({ data: null, loading: false, error: '' });
  const [attempt, setAttempt] = useState(0);
  const loadedAttempt = useRef(-1);
  const latestFetcher = useRef(fetcher);
  const mounted = useRef(true);

  useEffect(() => {
    latestFetcher.current = fetcher;
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || loadedAttempt.current === attempt) return;
    loadedAttempt.current = attempt;
    setState((s) => ({ ...s, loading: true, error: '' }));
    latestFetcher
      .current()
      .then((data) => mounted.current && setState({ data, loading: false, error: '' }))
      .catch((err) => mounted.current && setState((s) => ({ ...s, loading: false, error: err.message })));
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
};

const fetchStatus = () => adminWhatsAppService.getStatus();
const fetchEvents = () => adminSalesEventService.list();
// Ask for approved ones and keep only those again: the picker must never offer anything else.
const fetchApprovedTemplates = async () =>
  (await adminWhatsAppService.listTemplates({ status: WHATSAPP_TEMPLATE_STATUS.APPROVED })).filter(
    (t) => t.status === WHATSAPP_TEMPLATE_STATUS.APPROVED,
  );

/**
 * The lists the wizard reads from the server: whether sending is on (checked at once), the events
 * for the event-leads audience and the approved templates (each loaded when its step needs it).
 */
export default function useWizardLookups({ needsEvents, needsTemplates }) {
  return {
    status: useLookup(fetchStatus),
    events: useLookup(fetchEvents, needsEvents),
    templates: useLookup(fetchApprovedTemplates, needsTemplates),
  };
}
